import React, { useState } from 'react';
import {
    Box,
    Button,
    FormControlLabel,
    Icon,
    IconButton,
    MenuItem,
    Popover,
    Switch,
    Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import SectionCard from '../components/SectionCard';
import IbmVolumeDetailsDialog from '../components/IbmVolumeDetailsDialog';

export default function HeadNodePDSSection({ form, setField }) {
    const policies = useSelector(({ storage }) => storage.policies || []);

    const availableStorageConfigs = form.storageConfiguration?.configurations || [];
    const serverAttachedVolumes = form.serverAttachedVolumes || [];

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState(null);
    const [selectedStorageConfig, setSelectedStorageConfig] = useState(null);
    const [selectedPolicy, setSelectedPolicy] = useState(null);

    // Dropdown state
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

    // Info popover
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);

    // Don't render if no storage configs available
    if (!availableStorageConfigs.length) return null;

    const addVolume = (storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        const headVolume = config.headNodeVolumes?.[0];
        const newVolume = {
            id: `head-volume-${Date.now()}`,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            policyName: '',
            overrides: {
                capacity: headVolume?.overrides?.capacity || null,
                iops: headVolume?.overrides?.iops || null,
                throughput: headVolume?.overrides?.throughput || null,
            },
            status: 'pending',
            nfsEnabled: false,
            nfsExportOptions: 'rw,sync,no_subtree_check,no_root_squash',
        };
        setField('serverAttachedVolumes', [...serverAttachedVolumes, newVolume]);
        setDropdownOpen(false);
    };

    const removeVolume = (volumeId) => {
        setField('serverAttachedVolumes', serverAttachedVolumes.filter(v => v.id !== volumeId));
    };

    const updateVolume = (updatedVolume) => {
        const idx = serverAttachedVolumes.findIndex(v => v.id === updatedVolume.id);
        if (idx === -1) return;
        const updated = [...serverAttachedVolumes];
        updated[idx] = updatedVolume;
        setField('serverAttachedVolumes', updated);
    };

    const openEditDialog = (volume) => {
        const storageConfig = availableStorageConfigs.find(c => c.id === volume.storageConfigId);
        const policyId = storageConfig?.headNodeVolumes?.[0]?.policy;
        setSelectedVolume(volume);
        setSelectedStorageConfig(storageConfig || null);
        setSelectedPolicy(policyId ? policies.find(p => p._id === policyId) : null);
        setDialogOpen(true);
    };

    return (
        <>
            <SectionCard icon="storage" title="Head Node Performance Data Storage">
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                    Attach high-performance storage to head node.
                </Typography>

                {serverAttachedVolumes.length > 0 ? (
                    <Box mb={2}>
                        {serverAttachedVolumes.map((volume, volIndex) => {
                            if (!volume) return null;
                            const storageConfig = availableStorageConfigs.find(c => c.id === volume.storageConfigId);
                            const storageConfigName = storageConfig?.name || 'Unknown Storage';
                            const headVolume = storageConfig?.headNodeVolumes?.[0];
                            const policyId = headVolume?.policy;
                            const policy = policyId ? policies.find(p => p._id === policyId) : null;
                            const displaySize = volume.size
                                || volume.overrides?.capacity
                                || headVolume?.overrides?.capacity
                                || policy?.ibmParams?.capacity
                                || null;

                            return (
                                <Box
                                    key={volume.id || `volume-${volIndex}`}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                >
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: '#E3F2FD',
                                        borderRadius: '16px',
                                        padding: '4px 10px',
                                        height: '26px',
                                        border: '1px solid #BBDEFB',
                                        marginRight: '8px',
                                        marginBottom: '4px',
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', marginRight: '8px' }}>
                                            <Typography variant="body2" style={{
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                color: '#1565C0',
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {displaySize ? `${storageConfigName} (${displaySize}GB)` : storageConfigName}
                                            </Typography>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); openEditDialog(volume); }}
                                                style={{
                                                    padding: '1px', width: '16px', height: '16px',
                                                    color: '#1976D2', backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                }}
                                            >
                                                <Icon style={{ fontSize: '11px' }}>edit</Icon>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); removeVolume(volume.id); }}
                                                style={{
                                                    padding: '1px', width: '16px', height: '16px',
                                                    color: '#D32F2F', backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                }}
                                            >
                                                <Icon style={{ fontSize: '11px' }}>delete</Icon>
                                            </IconButton>
                                        </div>
                                    </div>

                                    <Box display="flex" alignItems="center" style={{ marginLeft: '16px', gap: '12px' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={volume.nfsEnabled || false}
                                                    onChange={(e) => {
                                                        const volumes = [...serverAttachedVolumes];
                                                        volumes[volIndex] = { ...volumes[volIndex], nfsEnabled: e.target.checked };
                                                        setField('serverAttachedVolumes', volumes);
                                                    }}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body1" style={{ fontWeight: 500, fontSize: '14px' }}>
                                                    Share via NFS
                                                </Typography>
                                            }
                                        />
                                        <IconButton size="small" onClick={(e) => setInfoAnchorEl(e.currentTarget)}>
                                            <Icon style={{ fontSize: '20px', color: '#2196F3' }}>info</Icon>
                                        </IconButton>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box mb={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px dashed #e0e0e0' }}>
                        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                            No volumes attached
                        </Typography>
                    </Box>
                )}

                {/* Info Popover */}
                <Popover
                    open={Boolean(infoAnchorEl)}
                    anchorEl={infoAnchorEl}
                    onClose={() => setInfoAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box p={2} style={{ maxWidth: '400px' }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '8px' }}>
                            Performance Data Storage
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            When enabled, this volume will be automatically shared with all compute nodes via NFS (Network File System). The volume will be mounted at the same path on all nodes, allowing for shared high-performance data access across your cluster. This is ideal for:
                        </Typography>
                        <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                            <li><Typography variant="body2" color="textSecondary">Shared datasets and input files</Typography></li>
                            <li><Typography variant="body2" color="textSecondary">Intermediate computation results</Typography></li>
                            <li><Typography variant="body2" color="textSecondary">Checkpoints and logs</Typography></li>
                            <li><Typography variant="body2" color="textSecondary">Collaborative workspaces</Typography></li>
                        </ul>
                        <Typography variant="body2" color="textSecondary">
                            All nodes will have read/write access to the shared storage.
                        </Typography>
                    </Box>
                </Popover>

                {/* Add Volume Button — only when no volumes exist (max 1) */}
                {serverAttachedVolumes.length === 0 && (
                    <Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Icon>add</Icon>}
                            onClick={(e) => {
                                setDropdownAnchorEl(e.currentTarget);
                                setDropdownOpen(true);
                            }}
                            disabled={availableStorageConfigs.length === 0}
                        >
                            Add Volume
                        </Button>
                        <Popover
                            open={dropdownOpen}
                            anchorEl={dropdownAnchorEl}
                            onClose={() => setDropdownOpen(false)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ style: { maxHeight: 300, minWidth: 250 } }}
                        >
                            {availableStorageConfigs.map((config) => (
                                <MenuItem key={config.id} onClick={() => addVolume(config.id)}>
                                    <Box display="flex" alignItems="center">
                                        <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                        <Box>
                                            <Typography variant="body2" style={{ fontSize: '12px' }}>
                                                {config.name || config.providerName || 'Unknown Provider'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" style={{ fontSize: '10px' }}>
                                                {config.providerType ? config.providerType.toUpperCase() : 'UNKNOWN'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))}
                            {availableStorageConfigs.length === 0 && (
                                <MenuItem disabled>No storage providers available</MenuItem>
                            )}
                        </Popover>
                    </Box>
                )}
            </SectionCard>

            <IbmVolumeDetailsDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setSelectedVolume(null); }}
                onSave={(vol) => { updateVolume(vol); setDialogOpen(false); }}
                volume={selectedVolume}
                storageConfig={selectedStorageConfig}
                policy={selectedPolicy}
                isEditable
            />
        </>
    );
}
